import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  adminCreateSubscriptionPlan,
  adminGetSubscriptionModels,
  adminGetSubscriptionPlans,
  adminUpdateSubscriptionPlan,
  adminListSubscriptionPlanImages,
  adminUploadSubscriptionPlanImage,
} from "../../api/subscriptionApi";
import "./AdminSubscriptionsPlans.css";

const euro = (cents) => {
  const value = Number(cents);
  if (!Number.isFinite(value)) return "—";

  return (value / 100).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// --- conversione prezzo euro <-> centesimi (solo per l'input; il payload
// inviato al backend resta sempre in price_cents, invariato) ---
function centsToEuroStr(cents) {
  if (cents === "" || cents == null) return "";
  const n = Number(cents);
  if (!Number.isFinite(n)) return "";
  return (n / 100).toFixed(2).replace(".", ",");
}

function euroStrToCents(str) {
  const normalized = String(str ?? "").replace(",", ".").trim();
  if (normalized === "") return "";
  const n = Number(normalized);
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 100));
}

function getModelCodeById(models, modelId) {
  const found = (models || []).find((m) => String(m.id) === String(modelId));
  return found?.code || "";
}

const MODEL_LABELS = {
  discount: "Sconto",
  credits: "Crediti",
  recurring: "Ricorrente",
};

const FREQUENCY_LABELS = {
  weekly: "Settimanale",
  biweekly: "Ogni 2 settimane",
  monthly: "Mensile",
};

function summarizeConfig(plan) {
  const modelCode = plan?.model_code;
  const cfg = plan?.config || {};

  if (modelCode === "discount") {
    const parts = [];
    if (cfg.discount_percent != null) parts.push(`Sconto ${cfg.discount_percent}%`);
    if (cfg.free_delivery_per_month) {
      parts.push(
        `${cfg.free_delivery_per_month} consegn${
          Number(cfg.free_delivery_per_month) === 1 ? "a" : "e"
        } gratis/mese`,
      );
    }
    return parts.join(" · ") || "—";
  }

  if (modelCode === "credits") {
    const parts = [];
    if (cfg.monthly_credits != null) {
      parts.push(`Credito ${euro(cfg.monthly_credits)}/mese`);
    }
    if (cfg.rollover_percent != null) {
      parts.push(`rollover ${cfg.rollover_percent}%`);
    }
    return parts.join(" · ") || "—";
  }

  if (modelCode === "recurring") {
    const parts = [];
    parts.push(FREQUENCY_LABELS[cfg.frequency] || cfg.frequency || "—");
    if (cfg.max_items != null) parts.push(`max ${cfg.max_items} capi`);
    if (Array.isArray(cfg.preferred_days) && cfg.preferred_days.length) {
      parts.push(`${cfg.preferred_days.length} giorni/sett.`);
    }
    return parts.join(" · ") || "—";
  }

  return "—";
}

const PRESENTATION_VARIANTS = {
  small: {
    label: "Small",
    color: "#0877d9",
    image_key: "folded-small",
  },
  medium: {
    label: "Medium",
    color: "#ec3f85",
    image_key: "folded-medium",
  },
  large: {
    label: "Large",
    color: "#0aa69e",
    image_key: "folded-large",
  },
  custom: {
    label: "Personalizzato",
    color: "#0877d9",
    image_key: "folded-small",
  },
};

const PRESENTATION_IMAGE_OPTIONS = [
  { value: "folded-small", label: "Pila small" },
  { value: "folded-medium", label: "Pila medium" },
  { value: "folded-large", label: "Pila large" },
];

function previewImageSrc(presentation, imageUrl) {
  const key = presentation?.image_key;
  if (key === "folded-small") return "/images/folded-small.png";
  if (key === "folded-medium") return "/images/folded-medium.png";
  if (key === "folded-large") return "/images/folded-large.png";
  return imageUrl || "/images/folded-small.png";
}

function buildDefaultPresentation(variant = "small") {
  const preset = PRESENTATION_VARIANTS[variant] || PRESENTATION_VARIANTS.small;

  return {
    variant,
    title: preset.label,
    short_description: "",
    theme_color: preset.color,
    image_key: preset.image_key,
    cta_label: "Attiva ora",
    rows: [
      { icon: "👕", label: "N° capi max", value: "" },
      { icon: "👔", label: "N° camicie max", value: "" },
      { icon: "🛏️", label: "N° lenzuola max", value: "" },
      { icon: "💶", label: "Costo al mese", value: "" },
    ],
    conditions_text:
      "Il numero indicato è il massimo dei capi che non può essere superato per ogni consegna.\nSe consegni meno capi non ricevi sconti.\nEventuali capi extra o speciali possono essere addebitati separatamente.",
  };
}

function normalizePresentationForForm(presentation = {}) {
  const variant = presentation.variant || "small";
  const preset = PRESENTATION_VARIANTS[variant] || PRESENTATION_VARIANTS.small;

  const rows =
    Array.isArray(presentation.rows) && presentation.rows.length
      ? presentation.rows
      : buildDefaultPresentation(variant).rows;

  const conditionsText = Array.isArray(presentation.conditions)
    ? presentation.conditions.join("\n")
    : presentation.conditions_text ||
      buildDefaultPresentation(variant).conditions_text;

  return {
    variant,
    title: presentation.title || preset.label,
    short_description: presentation.short_description || "",
    theme_color: presentation.theme_color || preset.color,
    image_key: presentation.image_key || preset.image_key,
    cta_label: presentation.cta_label || "Attiva ora",
    rows: rows.map((r) => ({
      icon: r.icon || "",
      label: r.label || "",
      value: r.value || "",
    })),
    conditions_text: conditionsText,
  };
}

function buildPresentationFromForm(presentation = {}) {
  const rows = Array.isArray(presentation.rows) ? presentation.rows : [];

  const cleanRows = rows
    .map((r) => ({
      icon: String(r.icon || "").trim(),
      label: String(r.label || "").trim(),
      value: String(r.value || "").trim(),
    }))
    .filter((r) => r.label || r.value || r.icon);

  const conditions = String(presentation.conditions_text || "")
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

  return {
    variant: presentation.variant || "custom",
    title: String(presentation.title || "").trim(),
    short_description: String(presentation.short_description || "").trim(),
    theme_color: String(presentation.theme_color || "#0877d9").trim(),
    image_key: String(presentation.image_key || "folded-small").trim(),
    cta_label: String(presentation.cta_label || "Attiva ora").trim(),
    rows: cleanRows,
    conditions,
  };
}

function buildDefaultConfigByModel(modelCode) {
  // "Formato card" è nascosto in UI (vedi PresentationConfigFields): il
  // valore di default è fissato a "small" per tutti i modelli.
  if (modelCode === "discount") {
    return {
      discount_percent: "",
      free_delivery_per_month: "",
      presentation: buildDefaultPresentation("small"),
    };
  }

  if (modelCode === "credits") {
    return {
      monthly_credits_eur: "",
      rollover_enabled: false,
      rollover_percent: "",
      presentation: buildDefaultPresentation("small"),
    };
  }

  if (modelCode === "recurring") {
    return {
      frequency: "weekly",
      max_items: "",
      preferred_days: [],
      presentation: buildDefaultPresentation("small"),
    };
  }

  return {};
}

function normalizeConfigForForm(modelCode, config = {}) {
  if (modelCode === "discount") {
    return {
      discount_percent: config.discount_percent ?? "",
      free_delivery_per_month: config.free_delivery_per_month ?? "",
      presentation: normalizePresentationForForm(config.presentation),
    };
  }

  if (modelCode === "credits") {
    return {
      monthly_credits_eur:
        config.monthly_credits != null
          ? String(Number(config.monthly_credits) / 100)
          : "",
      rollover_enabled: config.rollover_percent != null,
      rollover_percent: config.rollover_percent ?? "",
      presentation: normalizePresentationForForm(config.presentation),
    };
  }

  if (modelCode === "recurring") {
    return {
      frequency: config.frequency || "weekly",
      max_items: config.max_items ?? "",
      preferred_days: Array.isArray(config.preferred_days)
        ? config.preferred_days.map(Number)
        : [],
      presentation: normalizePresentationForForm(config.presentation),
    };
  }

  return {};
}

function buildConfigFromForm(modelCode, configForm = {}) {
  if (modelCode === "discount") {
    const discount = Number(configForm.discount_percent || 0);
    const freeDelivery = Number(configForm.free_delivery_per_month || 0);

    if (!Number.isFinite(discount) || discount < 0 || discount > 100) {
      throw new Error("Lo sconto deve essere tra 0 e 100");
    }

    if (!Number.isFinite(freeDelivery) || freeDelivery < 0) {
      throw new Error("Le consegne gratuite non possono essere negative");
    }

    return {
      discount_percent: discount,
      free_delivery_per_month: Math.floor(freeDelivery),
      presentation: buildPresentationFromForm(configForm.presentation),
    };
  }

  if (modelCode === "credits") {
    const creditsEur = Number(configForm.monthly_credits_eur || 0);
    const rollover = Number(configForm.rollover_percent || 0);

    if (!Number.isFinite(creditsEur) || creditsEur < 0) {
      throw new Error("Il credito mensile non può essere negativo");
    }

    if (
      configForm.rollover_enabled &&
      (!Number.isFinite(rollover) || rollover < 0 || rollover > 100)
    ) {
      throw new Error("Il rollover deve essere tra 0 e 100");
    }

    return {
      monthly_credits: Math.round(creditsEur * 100),
      rollover_percent: configForm.rollover_enabled ? rollover : null,
      presentation: buildPresentationFromForm(configForm.presentation),
    };
  }

  if (modelCode === "recurring") {
    const maxItems = Number(configForm.max_items || 0);

    if (!Number.isFinite(maxItems) || maxItems < 0) {
      throw new Error("Il numero massimo capi non può essere negativo");
    }

    return {
      frequency: configForm.frequency || "weekly",
      max_items: Math.floor(maxItems),
      preferred_days: Array.isArray(configForm.preferred_days)
        ? configForm.preferred_days.map(Number)
        : [],
      presentation: buildPresentationFromForm(configForm.presentation),
    };
  }

  return {};
}

function buildEmptyForm() {
  return {
    model_id: "",
    name: "",
    description: "",
    price_cents: "",
    billing_period: "monthly",
    config_form: {},
    image_url: "",
    is_active: true,
  };
}

function buildPayloadFromForm(form, models) {
  const modelCode = getModelCodeById(models, form.model_id);

  if (!form.model_id) throw new Error("Seleziona un tipo di piano");
  if (!modelCode) throw new Error("Tipo di piano non valido");
  if (!form.name.trim()) throw new Error("Inserisci il nome del piano");

  if (form.price_cents === "" || Number.isNaN(Number(form.price_cents))) {
    throw new Error("Inserisci un prezzo valido");
  }

  const priceCents = Number(form.price_cents);
  if (!Number.isFinite(priceCents) || priceCents < 0) {
    throw new Error("Il prezzo non può essere negativo");
  }

  const config = buildConfigFromForm(modelCode, form.config_form || {});

  return {
    model_id: Number(form.model_id),
    name: form.name.trim(),
    description: form.description.trim() || null,
    price_cents: Math.round(priceCents),
    billing_period: form.billing_period || "monthly",
    config,
    image_url: form.image_url.trim() || null,
    is_active: !!form.is_active,
  };
}

const DAYS = [
  { value: 1, label: "Lunedì" },
  { value: 2, label: "Martedì" },
  { value: 3, label: "Mercoledì" },
  { value: 4, label: "Giovedì" },
  { value: 5, label: "Venerdì" },
  { value: 6, label: "Sabato" },
  { value: 0, label: "Domenica" },
];

// Default preimpostati SOLO in creazione di un nuovo piano (form vuoto).
// In modifica di un piano esistente questa funzione non viene mai chiamata:
// il form viene invece popolato da normalizeConfigForForm() con i valori
// salvati del piano.
function applyNewPlanDefaults(baseForm, models) {
  const recurringModel = (models || []).find((m) => m.code === "recurring");
  if (!recurringModel) return baseForm;

  return {
    ...baseForm,
    model_id: String(recurringModel.id),
    config_form: {
      frequency: "weekly",
      max_items: "",
      preferred_days: DAYS.map((d) => d.value),
      presentation: buildDefaultPresentation("small"),
    },
  };
}

// Riassunto mostrato nell'header della sezione "Tipo di piano" quando è
// collassata.
function summarizeTypeSection(modelCode, configForm) {
  if (!modelCode) return "Nessun tipo selezionato";

  const cfg = configForm || {};
  const modelLabel = MODEL_LABELS[modelCode] || modelCode;

  if (modelCode === "recurring") {
    const freqLabel =
      FREQUENCY_LABELS[cfg.frequency] || cfg.frequency || "—";
    const days = Array.isArray(cfg.preferred_days) ? cfg.preferred_days : [];
    const daysLabel =
      days.length === DAYS.length
        ? "Tutti i giorni"
        : days.length === 0
          ? "Nessun giorno"
          : `${days.length} giorni/sett.`;

    return [modelLabel, freqLabel, daysLabel].join(" · ");
  }

  if (modelCode === "discount") {
    const parts = [modelLabel];
    if (cfg.discount_percent !== "" && cfg.discount_percent != null) {
      parts.push(`Sconto ${cfg.discount_percent}%`);
    }
    return parts.join(" · ");
  }

  if (modelCode === "credits") {
    const parts = [modelLabel];
    if (cfg.monthly_credits_eur !== "" && cfg.monthly_credits_eur != null) {
      parts.push(`${cfg.monthly_credits_eur} €/mese`);
    }
    return parts.join(" · ");
  }

  return modelLabel;
}

function SubscriptionConfigFields({ modelCode, value, onChange }) {
  const cfg = value || {};

  function setConfigField(key, nextValue) {
    onChange({
      ...cfg,
      [key]: nextValue,
    });
  }

  if (!modelCode) {
    return (
      <div className="subp-empty-box">
        Scegli un tipo di piano qui sopra per configurarne i dettagli.
      </div>
    );
  }

  if (modelCode === "discount") {
    return (
      <div className="subp-form-grid">
        <div className="subp-field">
          <label>Sconto (%)</label>
          <input
            className="subp-input"
            type="number"
            min="0"
            max="100"
            step="1"
            value={cfg.discount_percent ?? ""}
            onChange={(e) => setConfigField("discount_percent", e.target.value)}
            placeholder="Es. 10"
          />
        </div>

        <div className="subp-field">
          <label>Consegne gratuite al mese</label>
          <input
            className="subp-input"
            type="number"
            min="0"
            step="1"
            value={cfg.free_delivery_per_month ?? ""}
            onChange={(e) =>
              setConfigField("free_delivery_per_month", e.target.value)
            }
            placeholder="Es. 1"
          />
        </div>
      </div>
    );
  }

  if (modelCode === "credits") {
    return (
      <div className="subp-form-grid">
        <div className="subp-field">
          <label>Credito mensile (€)</label>
          <input
            className="subp-input"
            type="number"
            min="0"
            step="0.01"
            value={cfg.monthly_credits_eur ?? ""}
            onChange={(e) =>
              setConfigField("monthly_credits_eur", e.target.value)
            }
            placeholder="Es. 45"
          />
        </div>

        <div className="subp-field">
          <label className="subp-check-row">
            <input
              type="checkbox"
              checked={!!cfg.rollover_enabled}
              onChange={(e) =>
                setConfigField("rollover_enabled", e.target.checked)
              }
            />
            <span>Consenti rollover credito non usato</span>
          </label>
        </div>

        {cfg.rollover_enabled && (
          <div className="subp-field">
            <label>Percentuale rollover</label>
            <input
              className="subp-input"
              type="number"
              min="0"
              max="100"
              step="1"
              value={cfg.rollover_percent ?? ""}
              onChange={(e) =>
                setConfigField("rollover_percent", e.target.value)
              }
              placeholder="Es. 25"
            />
          </div>
        )}
      </div>
    );
  }

  if (modelCode === "recurring") {
    return (
      <div className="subp-form-grid">
        <div className="subp-field">
          <label>Frequenza</label>
          <select
            className="subp-select"
            value={cfg.frequency || "weekly"}
            onChange={(e) => setConfigField("frequency", e.target.value)}
          >
            <option value="weekly">Settimanale</option>
            <option value="biweekly">Ogni 2 settimane</option>
            <option value="monthly">Mensile</option>
          </select>
        </div>

        <div className="subp-field subp-field-full">
          <label>Giorni preferiti</label>

          <div className="subp-days-grid">
            {DAYS.map((day) => {
              const selected = (cfg.preferred_days || []).includes(day.value);

              return (
                <label
                  key={day.value}
                  className={`subp-day-check ${selected ? "is-selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={(e) => {
                      const current = Array.isArray(cfg.preferred_days)
                        ? cfg.preferred_days
                        : [];

                      const next = e.target.checked
                        ? [...current, day.value]
                        : current.filter((x) => x !== day.value);

                      setConfigField("preferred_days", next);
                    }}
                  />
                  <span>{day.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subp-empty-box">
      Tipo di piano non gestito: <strong>{modelCode}</strong>
    </div>
  );
}

function PresentationConfigFields({
  value,
  onChange,
  imageUrl,
  onChangeImageUrl,
  cloudinaryImages,
  imagesLoading,
  uploading,
  uploadError,
  onUploadImage,
}) {
  const cfg = value || buildDefaultPresentation("small");
  const images = cloudinaryImages || [];

  // Se il piano ha salvato un'immagine Cloudinary che oggi non risulta più
  // nell'elenco (es. list non ancora caricata), la teniamo comunque
  // selezionabile con un'opzione dedicata: la select non deve mai "saltare"
  // silenziosamente su un altro valore.
  const isCurrentImageOrphan =
    !!cfg.image_key &&
    /^https?:\/\//i.test(cfg.image_key) &&
    !images.some((img) => img.url === cfg.image_key);

  function selectImage(nextKey) {
    const isCloudinaryUrl = /^https?:\/\//i.test(nextKey);
    setField("image_key", nextKey);
    onChangeImageUrl(isCloudinaryUrl ? nextKey : "");
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permette di ricaricare lo stesso file due volte
    if (file && onUploadImage) onUploadImage(file);
  }

  function setField(key, nextValue) {
    onChange({
      ...cfg,
      [key]: nextValue,
    });
  }

  function setVariant(nextVariant) {
    const preset =
      PRESENTATION_VARIANTS[nextVariant] || PRESENTATION_VARIANTS.custom;

    onChange({
      ...cfg,
      variant: nextVariant,
      title: cfg.title || preset.label,
      theme_color: preset.color,
      image_key: preset.image_key,
    });
  }

  function updateRow(index, key, nextValue) {
    const rows = Array.isArray(cfg.rows) ? [...cfg.rows] : [];
    rows[index] = {
      ...(rows[index] || {}),
      [key]: nextValue,
    };
    setField("rows", rows);
  }

  function addRow() {
    const rows = Array.isArray(cfg.rows) ? cfg.rows : [];
    setField("rows", [...rows, { icon: "•", label: "", value: "" }]);
  }

  function removeRow(index) {
    const rows = Array.isArray(cfg.rows) ? cfg.rows : [];
    setField(
      "rows",
      rows.filter((_, i) => i !== index),
    );
  }

  return (
    <div className="subp-form-grid">
      {/* Nascosto in UI (valore fisso: Small) ma non rimosso da codice/logica:
          setVariant() e cfg.variant restano attivi e funzionanti. */}
      <div className="subp-field subp-field-hidden" aria-hidden="true">
        <label>Formato card</label>
        <select
          className="subp-select"
          value={cfg.variant || "small"}
          onChange={(e) => setVariant(e.target.value)}
          tabIndex={-1}
        >
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
          <option value="custom">Personalizzato</option>
        </select>
      </div>

      <div className="subp-field">
        <label>Titolo card</label>
        <input
          className="subp-input"
          type="text"
          value={cfg.title || ""}
          onChange={(e) => setField("title", e.target.value)}
          placeholder="Es. Small"
        />
      </div>

      <div className="subp-field">
        <label>Colore card</label>
        <input
          className="subp-input"
          type="color"
          value={cfg.theme_color || "#0877d9"}
          onChange={(e) => setField("theme_color", e.target.value)}
        />
      </div>

      <div className="subp-field">
        <label>
          Immagine decorativa{" "}
          <span className="subp-label-hint">(consigliata: 80×60 px)</span>
        </label>
        <select
          className="subp-select"
          value={cfg.image_key || "folded-small"}
          onChange={(e) => selectImage(e.target.value)}
        >
          {PRESENTATION_IMAGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}

          {images.length > 0 && (
            <optgroup label="Immagini caricate">
              {images.map((img) => (
                <option key={img.public_id} value={img.url}>
                  {img.name}
                </option>
              ))}
            </optgroup>
          )}

          {isCurrentImageOrphan && (
            <option value={cfg.image_key}>
              Immagine corrente (non più in elenco)
            </option>
          )}
        </select>
      </div>

      <div className="subp-field subp-field-full">
        <label>Carica una nuova immagine decorativa</label>
        <div className="subp-upload-row">
          <label
            htmlFor="subp-decorative-image-upload"
            className={`subp-btn subp-btn-ghost subp-btn-sm ${
              uploading ? "is-disabled" : ""
            }`}
          >
            {uploading ? "Caricamento..." : "Scegli file"}
          </label>
          <input
            id="subp-decorative-image-upload"
            className="subp-file-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            disabled={uploading}
            onChange={handleFileChange}
          />
          {imagesLoading && (
            <span className="subp-hint">Aggiornamento elenco...</span>
          )}
        </div>
        {uploadError && <p className="subp-upload-error">{uploadError}</p>}
        <p className="subp-hint">
          JPG, PNG, WEBP o GIF, max 3MB. Dopo il caricamento l'immagine viene
          selezionata automaticamente.
        </p>
      </div>

      <div className="subp-field subp-field-full">
        <label>Descrizione breve</label>
        <textarea
          className="subp-textarea"
          rows="2"
          value={cfg.short_description || ""}
          onChange={(e) => setField("short_description", e.target.value)}
          placeholder="Es. Ideale per chi vuole tenere sotto controllo lo stiro ogni settimana."
        />
      </div>

      <div className="subp-field">
        <label>Testo del pulsante (CTA)</label>
        <input
          className="subp-input"
          type="text"
          value={cfg.cta_label || ""}
          onChange={(e) => setField("cta_label", e.target.value)}
          placeholder="Attiva ora"
        />
      </div>

      <div className="subp-field subp-field-full">
        <label>Dettagli mostrati nella card</label>

        <div className="subp-rows-editor">
          {(cfg.rows || []).map((row, index) => (
            <div className="subp-row-editor" key={index}>
              <input
                className="subp-input subp-row-icon"
                value={row.icon || ""}
                onChange={(e) => updateRow(index, "icon", e.target.value)}
                placeholder="👕"
                aria-label="Icona"
              />

              <input
                className="subp-input subp-row-label"
                value={row.label || ""}
                onChange={(e) => updateRow(index, "label", e.target.value)}
                placeholder="N° pantaloni max"
                aria-label="Etichetta"
              />

              <input
                className="subp-input subp-row-value"
                value={row.value || ""}
                onChange={(e) => updateRow(index, "value", e.target.value)}
                placeholder="Es. 4"
                aria-label="Valore"
              />

              <button
                type="button"
                className="subp-row-remove"
                onClick={() => removeRow(index)}
                aria-label="Rimuovi riga"
                title="Rimuovi riga"
              >
                ×
              </button>
            </div>
          ))}

          <button
            type="button"
            className="subp-btn subp-btn-ghost subp-btn-sm"
            onClick={addRow}
          >
            + Aggiungi riga
          </button>
        </div>
      </div>

      <div className="subp-field subp-field-full">
        <label>Condizioni</label>
        <textarea
          className="subp-textarea"
          rows="5"
          value={cfg.conditions_text || ""}
          onChange={(e) => setField("conditions_text", e.target.value)}
          placeholder="Una condizione per riga"
        />
        <p className="subp-hint">
          Scrivi una condizione per riga. Verranno mostrate nella pagina
          confronto.
        </p>
      </div>
    </div>
  );
}

function PlanCardPreview({ presentation, imageUrl }) {
  const cfg = presentation || buildDefaultPresentation("small");
  const rows = (Array.isArray(cfg.rows) ? cfg.rows : []).filter(
    (r) => r.label || r.value,
  );
  const conditions = String(cfg.conditions_text || "")
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

  return (
    <div>
      <span className="subp-preview-label">Anteprima per il cliente</span>
      <div
        className="subp-preview-card"
        style={{ "--subp-preview-color": cfg.theme_color || "#0877d9" }}
      >
        <img
          className="subp-preview-img"
          src={previewImageSrc(cfg, imageUrl)}
          alt=""
          aria-hidden="true"
        />

        <div className="subp-preview-body">
          <h3 className="subp-preview-title">{cfg.title || "Titolo piano"}</h3>

          {!!cfg.short_description && (
            <p className="subp-preview-desc">{cfg.short_description}</p>
          )}

          {rows.length > 0 && (
            <div className="subp-preview-rows">
              {rows.map((row, i) => (
                <div className="subp-preview-row" key={i}>
                  <span className="subp-preview-row-icon">
                    {row.icon || "•"}
                  </span>
                  <span className="subp-preview-row-label">{row.label}</span>
                  <span className="subp-preview-row-value">{row.value}</span>
                </div>
              ))}
            </div>
          )}

          <span className="subp-preview-cta">
            {cfg.cta_label || "Attiva ora"}
          </span>

          {conditions.length > 0 && (
            <div className="subp-preview-conditions">
              {conditions.map((c, i) => (
                <div key={i}>• {c}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminSubscriptionsPlans() {
  const token = localStorage.getItem("token");

  const [models, setModels] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(buildEmptyForm());
  const [priceEuroInput, setPriceEuroInput] = useState("");
  const [typeSectionOpen, setTypeSectionOpen] = useState(false);

  const [decorativeImages, setDecorativeImages] = useState([]);
  const [decorativeImagesLoading, setDecorativeImagesLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadImageError, setUploadImageError] = useState("");

  const modelOptions = useMemo(() => models || [], [models]);

  const currentModelCode = useMemo(
    () => getModelCodeById(models, form.model_id),
    [models, form.model_id],
  );

  async function loadAll() {
    if (!token) return;

    setLoading(true);
    try {
      const [modelsRes, plansRes] = await Promise.all([
        adminGetSubscriptionModels(token),
        adminGetSubscriptionPlans(token, { includeInactive }),
      ]);

      const loadedModels = modelsRes?.models || [];
      setModels(loadedModels);
      setPlans(plansRes?.plans || []);

      // Default preimpostati SOLO per un piano nuovo: form ancora vergine
      // (nessun modello scelto) e non in modifica. Se l'utente ha già
      // selezionato qualcosa, o sta modificando un piano esistente, non
      // tocchiamo il form.
      setForm((prev) => {
        if (editingPlan) return prev;
        if (prev.model_id) return prev;
        return applyNewPlanDefaults(prev, loadedModels);
      });
    } catch (err) {
      toast.error(err.message || "Errore caricamento abbonamenti");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  async function loadDecorativeImages() {
    if (!token) return;

    setDecorativeImagesLoading(true);
    try {
      const res = await adminListSubscriptionPlanImages(token);
      setDecorativeImages(res?.images || []);
    } catch (err) {
      toast.error(err.message || "Errore caricamento immagini decorative");
    } finally {
      setDecorativeImagesLoading(false);
    }
  }

  useEffect(() => {
    loadDecorativeImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleUploadDecorativeImage(file) {
    if (!token) return;

    setUploadImageError("");
    setUploadingImage(true);
    try {
      const res = await adminUploadSubscriptionPlanImage(file, token);
      const uploaded = res?.image;

      await loadDecorativeImages();

      // Auto-selezione: l'immagine appena caricata diventa quella del piano
      // in lavorazione (sia in config_form.presentation che a livello di
      // piano, per coerenza con previewImageSrc()).
      if (uploaded?.url) {
        setForm((prev) => ({
          ...prev,
          image_url: uploaded.url,
          config_form: {
            ...prev.config_form,
            presentation: {
              ...(prev.config_form?.presentation ||
                buildDefaultPresentation("small")),
              image_key: uploaded.url,
            },
          },
        }));
      }

      toast.success("Immagine caricata");
    } catch (err) {
      setUploadImageError(err.message || "Errore caricamento immagine");
    } finally {
      setUploadingImage(false);
    }
  }

  function onChangeField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function onChangePriceEuro(rawValue) {
    setPriceEuroInput(rawValue);
    onChangeField("price_cents", euroStrToCents(rawValue));
  }

  function onChangeModel(modelId) {
    const modelCode = getModelCodeById(models, modelId);

    setForm((prev) => ({
      ...prev,
      model_id: modelId,
      config_form: buildDefaultConfigByModel(modelCode),
    }));
  }

  function startEdit(plan) {
    setEditingPlan(plan);

    const nextForm = {
      model_id: String(plan.model_id ?? ""),
      name: plan.name || "",
      description: plan.description || "",
      price_cents: String(plan.price_cents ?? ""),
      billing_period: plan.billing_period || "monthly",
      config_form: normalizeConfigForForm(plan.model_code, plan.config || {}),
      image_url: plan.image_url || "",
      is_active: !!plan.is_active,
    };

    setForm(nextForm);
    setPriceEuroInput(centsToEuroStr(nextForm.price_cents));

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function resetForm() {
    setEditingPlan(null);
    setForm(applyNewPlanDefaults(buildEmptyForm(), models));
    setPriceEuroInput("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!token) return;

    let payload;
    try {
      payload = buildPayloadFromForm(form, models);
    } catch (err) {
      toast.error(err.message || "Dati form non validi");
      return;
    }

    setSaving(true);
    try {
      if (editingPlan?.id) {
        await adminUpdateSubscriptionPlan(editingPlan.id, payload, token);
        toast.success("Piano aggiornato");
      } else {
        await adminCreateSubscriptionPlan(payload, token);
        toast.success("Piano creato");
      }

      resetForm();
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Errore salvataggio piano");
    } finally {
      setSaving(false);
    }
  }

  const currentPresentation = form.config_form?.presentation;

  return (
    <div className="subp-page">
      <div className="subp-inner">
        <header className="subp-header">
          <div>
            <h1>Abbonamenti · Piani</h1>
            <p className="subp-header-sub">
              Crea e modifica i piani abbonamento disponibili per i fornitori.
            </p>
          </div>
        </header>

        <div className="subp-card">
          {editingPlan && (
            <div className="subp-edit-banner">
              <div>
                <span className="subp-edit-banner-text">
                  Stai modificando:
                </span>{" "}
                <strong>{editingPlan.name}</strong>
              </div>
              <button
                type="button"
                className="subp-btn subp-btn-ghost subp-btn-sm"
                onClick={resetForm}
                disabled={saving}
              >
                Annulla e crea nuovo
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* SEZIONE 1 — Tipo di piano (collassabile, collassata di default) */}
            <div className="subp-section">
              <button
                type="button"
                className="subp-section-head subp-section-head-toggle"
                onClick={() => setTypeSectionOpen((v) => !v)}
                aria-expanded={typeSectionOpen}
              >
                <span className="subp-section-num">1</span>
                <div className="subp-section-head-text">
                  <h3 className="subp-section-title">Tipo di piano</h3>
                  {typeSectionOpen ? (
                    <p className="subp-section-sub">
                      Scegli come funziona l'abbonamento: i campi sotto
                      cambiano in base alla scelta.
                    </p>
                  ) : (
                    <p className="subp-section-sub subp-section-summary">
                      {summarizeTypeSection(currentModelCode, form.config_form)}
                    </p>
                  )}
                </div>
                <span
                  className={`subp-section-chevron ${typeSectionOpen ? "is-open" : ""}`}
                  aria-hidden="true"
                >
                  ▾
                </span>
              </button>

              {typeSectionOpen && (
                <>
                  <div className="subp-form-grid">
                    <div className="subp-field">
                      <label>Modello</label>
                      <select
                        className="subp-select"
                        value={form.model_id}
                        onChange={(e) => onChangeModel(e.target.value)}
                      >
                        <option value="">Seleziona un tipo di piano</option>
                        {modelOptions.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name ||
                              MODEL_LABELS[model.code] ||
                              model.code}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="subp-config-box">
                    <SubscriptionConfigFields
                      modelCode={currentModelCode}
                      value={form.config_form}
                      onChange={(nextConfig) =>
                        onChangeField("config_form", nextConfig)
                      }
                    />
                  </div>
                </>
              )}
            </div>

            {/* SEZIONE 2 — Dati del piano */}
            <div className="subp-section">
              <div className="subp-section-head">
                <span className="subp-section-num">2</span>
                <div>
                  <h3 className="subp-section-title">Dati del piano</h3>
                  <p className="subp-section-sub">
                    Nome, prezzo e stato del piano.
                  </p>
                </div>
              </div>

              <div className="subp-form-grid">
                <div className="subp-field">
                  <label>Nome piano</label>
                  <input
                    className="subp-input"
                    type="text"
                    value={form.name}
                    onChange={(e) => onChangeField("name", e.target.value)}
                    placeholder="Es. Comfort Mensile"
                  />
                </div>

                <div className="subp-field">
                  <label>Prezzo al mese</label>
                  <div className="subp-input-wrap">
                    <input
                      className="subp-input"
                      type="text"
                      inputMode="decimal"
                      value={priceEuroInput}
                      onChange={(e) => onChangePriceEuro(e.target.value)}
                      placeholder="Es. 39,00"
                    />
                    <span className="subp-input-suffix">€</span>
                  </div>
                </div>

                <div className="subp-field">
                  <label>Periodo di fatturazione</label>
                  <div className="subp-static-value">Mensile</div>
                  <p className="subp-hint">
                    Per ora è disponibile solo la fatturazione mensile.
                  </p>
                </div>

                {/* Spostato qui dalla Sezione 1 (era dentro "Tipo di piano" ->
                    ramo Ricorrente). Stesso campo, stesso salvataggio
                    (config_form.max_items); visibile solo per il modello
                    Ricorrente, unico che lo usa. */}
                {currentModelCode === "recurring" && (
                  <div className="subp-field">
                    <label>Numero massimo capi</label>
                    <input
                      className="subp-input"
                      type="number"
                      min="0"
                      step="1"
                      value={form.config_form?.max_items ?? ""}
                      onChange={(e) =>
                        onChangeField("config_form", {
                          ...form.config_form,
                          max_items: e.target.value,
                        })
                      }
                      placeholder="Es. 20"
                    />
                  </div>
                )}

                <div className="subp-field">
                  <label className="subp-check-row">
                    <input
                      type="checkbox"
                      checked={!!form.is_active}
                      onChange={(e) =>
                        onChangeField("is_active", e.target.checked)
                      }
                    />
                    <span>Piano attivo</span>
                  </label>
                  <p className="subp-hint">
                    Se attivo, i clienti lo vedono e possono abbonarsi. Se
                    disattivato resta salvato ma nascosto.
                  </p>
                </div>

                <div className="subp-field subp-field-full">
                  <label>Descrizione</label>
                  <textarea
                    className="subp-textarea"
                    rows="3"
                    value={form.description}
                    onChange={(e) =>
                      onChangeField("description", e.target.value)
                    }
                    placeholder="Descrizione del piano"
                  />
                </div>
              </div>
            </div>

            {/* SEZIONE 3 — Come appare ai clienti */}
            <div className="subp-section">
              <div className="subp-section-head">
                <span className="subp-section-num">3</span>
                <div>
                  <h3 className="subp-section-title">
                    Come appare ai clienti
                  </h3>
                  <p className="subp-section-sub">
                    L'anteprima a destra si aggiorna mentre compili.
                  </p>
                </div>
              </div>

              <div className="subp-presentation-layout">
                <PresentationConfigFields
                  value={currentPresentation}
                  onChange={(nextPresentation) =>
                    onChangeField("config_form", {
                      ...form.config_form,
                      presentation: nextPresentation,
                    })
                  }
                  imageUrl={form.image_url}
                  onChangeImageUrl={(value) =>
                    onChangeField("image_url", value)
                  }
                  cloudinaryImages={decorativeImages}
                  imagesLoading={decorativeImagesLoading}
                  uploading={uploadingImage}
                  uploadError={uploadImageError}
                  onUploadImage={handleUploadDecorativeImage}
                />

                <div className="subp-preview-sticky">
                  <PlanCardPreview
                    presentation={currentPresentation}
                    imageUrl={form.image_url}
                  />
                </div>
              </div>
            </div>

            <div className="subp-form-actions">
              <button
                type="submit"
                className="subp-btn subp-btn-primary"
                disabled={saving}
              >
                {saving
                  ? "Salvataggio..."
                  : editingPlan
                    ? "Aggiorna piano"
                    : "Crea piano"}
              </button>

              <button
                type="button"
                className="subp-btn subp-btn-ghost"
                onClick={resetForm}
                disabled={saving}
              >
                {editingPlan ? "Annulla modifica" : "Svuota form"}
              </button>
            </div>
          </form>
        </div>

        <div className="subp-card">
          <div className="subp-card-head">
            <div>
              <h2>Elenco piani</h2>
              <p>Panoramica completa dei piani configurati.</p>
            </div>

            <label className="subp-check-row">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              <span>Mostra anche inattivi</span>
            </label>
          </div>

          {loading ? (
            <div className="subp-empty-box">Caricamento piani...</div>
          ) : plans.length === 0 ? (
            <div className="subp-empty-box">Nessun piano configurato.</div>
          ) : (
            <div className="subp-table-wrap">
              <table className="subp-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Piano</th>
                    <th>Modello</th>
                    <th className="is-right">Prezzo</th>
                    <th>Periodo</th>
                    <th>Stato</th>
                    <th>Immagine</th>
                    <th>Configurazione</th>
                    <th className="is-right">Azioni</th>
                  </tr>
                </thead>

                <tbody>
                  {plans.map((plan) => (
                    <tr key={plan.id}>
                      <td>{plan.id}</td>

                      <td>
                        <div className="subp-plan-name">{plan.name}</div>
                        {plan.description ? (
                          <div className="subp-plan-desc">
                            {plan.description}
                          </div>
                        ) : null}
                      </td>

                      <td>
                        <div className="subp-model-name">
                          {plan.model_name}
                        </div>
                        <div className="subp-model-code">
                          {plan.model_code}
                        </div>
                      </td>

                      <td className="is-right">{euro(plan.price_cents)}</td>
                      <td>{plan.billing_period}</td>

                      <td>
                        <span
                          className={`subp-badge ${
                            plan.is_active ? "subp-badge--on" : "subp-badge--off"
                          }`}
                        >
                          {plan.is_active ? "Attivo" : "Inattivo"}
                        </span>
                      </td>

                      <td>
                        {plan.image_url ? (
                          <a
                            href={plan.image_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Apri
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td>{summarizeConfig(plan)}</td>

                      <td className="is-right">
                        <button
                          type="button"
                          className="subp-btn subp-btn-ghost subp-btn-sm"
                          onClick={() => startEdit(plan)}
                        >
                          Modifica
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
